import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import api from '../services/api';

const ShoppingListContext = createContext();
const STORAGE_KEY = 'strixly_shopping_list';
const MIGRATION_KEY = 'strixly_sl_migrated';

// Einfache Frontend-Blacklist — spiegelt backend/services/moderation_service.py
const _BLOCKED = [
  'nazi', 'heil hitler', 'sieg heil', 'nsdap', 'neonazi', 'neo-nazi',
  'nigger', 'neger', 'kanake', 'judensau', 'jude verrecke', 'ausländer raus',
  'white power', 'white supremacy', 'untermensch', 'arische', 'arier',
  'fick dich', 'hurensohn', 'wichser', 'motherfucker', 'faggot',
  'tötet alle', 'kill all', 'death to',
  'kinderporno', 'kindersex', 'kinder porno', 'kinder sex', 'kinder porn',
  'child porn', 'childporn', 'child sex', 'childsex',
  'pedo', 'pedophil', 'pädophil', 'jailbait', 'lolita',
  'porno', 'pornos', 'porn', 'pornografie', 'pornography',
  'sex video', 'sexvideo', 'nacktbild', 'nacktbilder', 'nackt',
  'jihad', 'dschihad', 'isis', 'daesh', 'al qaida', 'al-qaida',
  'bombe bauen', 'anschlag', 'terroranschlag',
];

function _normalize(text) {
  return text
    .toLowerCase()
    .replace(/[0@]/g, 'o')
    .replace(/1/g, 'i')
    .replace(/3/g, 'e')
    .replace(/\s+/g, ' ')
    .trim();
}

const _blockedRe = new RegExp(
  _BLOCKED.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|'),
  'i'
);
const _KINDER_COMBO = /sex|porno?|porn|nackt|missbrauch|abuse/i;
function _isBlocked(text) {
  const n = _normalize(text);
  if (_blockedRe.test(n)) return true;
  if (/kinder|kind\b/i.test(n) && _KINDER_COMBO.test(n)) return true;
  return false;
}

// Parst eine Mengenangabe in {num, unit}
function parseAmount(str) {
  if (!str) return { num: null, unit: '' };
  str = str.trim();
  const fracMatch = str.match(/^(\d+)\/(\d+)\s*(.*)$/);
  if (fracMatch) {
    return {
      num: parseFloat(fracMatch[1]) / parseFloat(fracMatch[2]),
      unit: fracMatch[3].trim().toLowerCase(),
    };
  }
  const numMatch = str.match(/^(\d+(?:[.,]\d+)?)\s*(.*)$/);
  if (numMatch) {
    return {
      num: parseFloat(numMatch[1].replace(',', '.')),
      unit: numMatch[2].trim().toLowerCase(),
    };
  }
  return { num: null, unit: str.toLowerCase() };
}

function formatNum(n) {
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(1).replace('.', ',');
}

function mergeAmounts(a, b) {
  if (!a) return b || '';
  if (!b) return a;
  const pa = parseAmount(a);
  const pb = parseAmount(b);
  if (pa.num !== null && pb.num !== null && pa.unit === pb.unit) {
    const total = pa.num + pb.num;
    return formatNum(total) + (pa.unit ? ' ' + pa.unit : '');
  }
  return a + ' + ' + b;
}

// Lokaler Fallback-State beim Laden
function loadLocalItems() {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return stored.map(item => ({ ...item, id: item.id ?? (Date.now() + Math.random()) }));
  } catch { return []; }
}

export function ShoppingListProvider({ children }) {
  const [items, setItems] = useState(loadLocalItems);
  const [synced, setSynced] = useState(false);
  const syncedRef = useRef(false);

  // Beim Start: Daten vom Backend laden + ggf. Migration
  useEffect(() => {
    let cancelled = false;
    async function init() {
      try {
        const data = await api.getShoppingList();
        if (cancelled) return;

        // Einmalige Migration: localStorage-Items ans Backend pushen
        if (!localStorage.getItem(MIGRATION_KEY)) {
          const local = loadLocalItems();
          if (local.length > 0) {
            try {
              await api.addShoppingItems(local);
              const fresh = await api.getShoppingList();
              if (!cancelled) setItems(fresh.items || []);
            } catch {
              if (!cancelled) setItems(data.items || []);
            }
          } else {
            if (!cancelled) setItems(data.items || []);
          }
          localStorage.setItem(MIGRATION_KEY, '1');
          localStorage.removeItem(STORAGE_KEY);
        } else {
          if (!cancelled) setItems(data.items || []);
        }

        syncedRef.current = true;
        if (!cancelled) setSynced(true);
      } catch {
        // API nicht erreichbar → localStorage-Fallback bleibt aktiv
        syncedRef.current = false;
      }
    }
    init();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // localStorage-Fallback: nur schreiben wenn Backend nicht erreichbar
  useEffect(() => {
    if (!synced) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    }
  }, [items, synced]);

  async function addItems(newItems) {
    if (!syncedRef.current) {
      // Offline: nur lokal
      setItems(prev => _mergeLocally(prev, newItems));
      return;
    }
    // Optimistisch lokal einfügen
    setItems(prev => _mergeLocally(prev, newItems));
    try {
      const data = await api.addShoppingItems(newItems);
      setItems(data.items || []);
    } catch {
      // bleibt beim optimistischen Stand
    }
  }

  async function toggleItem(id) {
    // Optimistisch
    setItems(prev => prev.map(i => i.id === id ? { ...i, checked: !i.checked } : i));
    if (!syncedRef.current) return;
    try {
      await api.toggleShoppingItem(id);
    } catch {
      // Rollback
      setItems(prev => prev.map(i => i.id === id ? { ...i, checked: !i.checked } : i));
    }
  }

  async function removeItem(id) {
    const snapshot = items;
    setItems(prev => prev.filter(i => i.id !== id));
    if (!syncedRef.current) return;
    try {
      await api.deleteShoppingItem(id);
    } catch {
      setItems(snapshot);
    }
  }

  function addManual(name) {
    if (!name.trim()) return false;
    if (_isBlocked(name)) return false;
    addItems([{ name: name.trim(), amount: '', recipeId: null }]);
    return true;
  }

  async function clearChecked() {
    const snapshot = items;
    setItems(prev => prev.filter(i => !i.checked));
    if (!syncedRef.current) return;
    try {
      await api.clearCheckedShoppingItems();
    } catch {
      setItems(snapshot);
    }
  }

  async function clearAll() {
    const snapshot = items;
    setItems([]);
    if (!syncedRef.current) return;
    try {
      await api.clearAllShoppingItems();
    } catch {
      setItems(snapshot);
    }
  }

  const uncheckedCount = items.filter(i => !i.checked).length;

  return (
    <ShoppingListContext.Provider value={{ items, addItems, toggleItem, removeItem, addManual, clearChecked, clearAll, uncheckedCount }}>
      {children}
    </ShoppingListContext.Provider>
  );
}

export function useShoppingList() {
  return useContext(ShoppingListContext);
}

// ── Hilfsfunktion: optimistisches Mergen (wie Backend-Logik) ────────────────
function _mergeLocally(prev, newItems) {
  const result = [...prev];
  for (const item of newItems) {
    const key = (item.name || '').toLowerCase().trim();
    if (!key) continue;
    const existing = result.find(i => i.name.toLowerCase().trim() === key && !i.checked);
    if (existing) {
      existing.amount = mergeAmounts(existing.amount, item.amount || '');
    } else {
      result.push({
        id: Date.now() + Math.random(),
        name: item.name,
        amount: item.amount || '',
        recipeId: item.recipeId ?? null,
        checked: false,
      });
    }
  }
  return result;
}
